import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Briefcase, FolderKanban, CheckCircle2, Paperclip, Plus, ArrowRight, RefreshCw, Cloud, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user, getAuthHeaders, openclawSyncing, syncWithOpenClaw } = useAuth();
  const [stats, setStats] = useState({ clients: 0, projects: 0, tasks: 0, completed_tasks: 0, files: 0 });
  const [recentProjects, setRecentProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openclawStatus, setOpenclawStatus] = useState(null);

  const fetchData = async () => {
    try {
      const [statsRes, projectsRes] = await Promise.all([
        axios.get(`${API}/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/projects`, { headers: getAuthHeaders() })
      ]);
      setStats(statsRes.data);
      setRecentProjects(projectsRes.data.slice(0, 5));
    } catch (error) {
      console.error("Dashboard fetch error:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const fetchOpenClawStatus = async () => {
    try {
      const response = await axios.get(`${API}/openclaw/status`, { headers: getAuthHeaders() });
      setOpenclawStatus(response.data);
    } catch (error) {
      console.error("OpenClaw status error:", error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchOpenClawStatus();
  }, [getAuthHeaders]);

  const handleManualSync = async () => {
    toast.info("Sincronizando com OpenClaw...");
    const result = await syncWithOpenClaw();
    if (result?.status === "ok") {
      toast.success("Sincronização iniciada!");
      setTimeout(() => {
        fetchData();
        fetchOpenClawStatus();
      }, 3000);
    } else {
      toast.error(result?.message || "Erro na sincronização");
    }
  };

  const statCards = [
    { title: "Clientes", value: stats.clients, icon: Briefcase, color: "text-[#0A1628]", bg: "bg-[#0A1628]/10" },
    { title: "Projetos", value: stats.projects, icon: FolderKanban, color: "text-[#00A7E1]", bg: "bg-[#00A7E1]/10" },
    { title: "Tarefas Concluídas", value: stats.completed_tasks, icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-500/10" },
    { title: "Arquivos", value: stats.files, icon: Paperclip, color: "text-[#00A7E1]", bg: "bg-[#00A7E1]/10" },
  ];

  if (loading) {
    return (
      <div className="p-8 space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="animate-pulse rounded-xl">
              <CardContent className="p-6">
                <div className="h-20 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8" data-testid="dashboard-page">
      {/* Hero Section - Crowd SA Style */}
      <div className="hero-gradient rounded-2xl p-8 text-white">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="space-y-2">
            <p className="text-white/60 text-sm font-medium">Bem-vindo de volta</p>
            <h1 className="text-3xl lg:text-4xl font-bold heading-font">
              Olá, {user?.name?.split(" ")[0]}!
            </h1>
            <p className="text-white/70 max-w-md">
              Acompanhe seus projetos e clientes em tempo real
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button 
              variant="outline" 
              onClick={handleManualSync}
              disabled={openclawSyncing}
              className="gap-2 bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white"
              data-testid="openclaw-sync-btn"
            >
              <RefreshCw className={`h-4 w-4 ${openclawSyncing ? 'animate-spin' : ''}`} />
              {openclawSyncing ? 'Sincronizando...' : 'Sync OpenClaw'}
            </Button>
            <Link to="/clients">
              <Button className="bg-[#00A7E1] hover:bg-[#0095c8] text-[#0A1628] font-semibold" data-testid="new-client-btn">
                <Plus className="mr-2 h-4 w-4" />
                Novo Cliente
              </Button>
            </Link>
          </div>
        </div>

        {/* OpenClaw Status inside hero */}
        {openclawStatus && (
          <div className={`mt-6 flex items-center gap-3 p-3 rounded-lg ${openclawStatus.openclaw_online ? 'bg-emerald-500/20' : 'bg-amber-500/20'}`}>
            <Cloud className="h-5 w-5" />
            <div className="flex-1">
              <span className="font-medium">
                OpenClaw: {openclawStatus.openclaw_online ? 'Conectado' : 'Offline'}
              </span>
              {openclawStatus.last_sync && (
                <span className="text-sm ml-2 opacity-75">
                  • Último sync: {new Date(openclawStatus.last_sync).toLocaleString('pt-BR')}
                  {openclawStatus.synced_boards > 0 && ` (${openclawStatus.synced_boards} boards)`}
                </span>
              )}
            </div>
            {openclawSyncing && (
              <span className="text-sm animate-pulse">Sincronizando...</span>
            )}
          </div>
        )}
      </div>

      {/* Stats Grid - Control Room Style */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {statCards.map((stat, index) => (
          <Card 
            key={index} 
            className="rounded-xl hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 border-0 shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:border dark:border-white/5 dark:shadow-none" 
            data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s/g, "-")}`}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                  <p className="text-3xl lg:text-4xl font-bold heading-font">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-xl ${stat.bg}`}>
                  <stat.icon className={`h-5 w-5 lg:h-6 lg:w-6 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Projects - Updated Style */}
      <Card className="rounded-xl border-0 shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:border dark:border-white/5 dark:shadow-none">
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#00A7E1]/10">
              <TrendingUp className="h-5 w-5 text-[#00A7E1]" />
            </div>
            <CardTitle className="heading-font text-xl">Projetos Recentes</CardTitle>
          </div>
          <Link to="/overview">
            <Button variant="ghost" size="sm" className="text-[#00A7E1] hover:text-[#0095c8] hover:bg-[#00A7E1]/10" data-testid="view-all-projects-btn">
              Ver todos
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {recentProjects.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-[#00A7E1]/10 flex items-center justify-center mb-4">
                <FolderKanban className="h-8 w-8 text-[#00A7E1]" />
              </div>
              <p className="text-lg font-medium">Nenhum projeto ainda</p>
              <p className="text-muted-foreground mt-1">Comece criando seu primeiro cliente</p>
              <Link to="/clients">
                <Button className="mt-6 bg-[#00A7E1] hover:bg-[#0095c8] text-[#0A1628]" data-testid="create-first-project-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  Criar primeiro cliente
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-2">
              {recentProjects.map((project) => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="flex items-center gap-4 p-4 rounded-xl hover:bg-muted/50 transition-all duration-200 group"
                  data-testid={`project-link-${project.id}`}
                >
                  <div
                    className="h-12 w-12 rounded-xl bg-cover bg-center shrink-0 shadow-sm"
                    style={{ backgroundImage: `url(${project.cover_image})` }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate group-hover:text-[#00A7E1] transition-colors">{project.name}</p>
                    <p className="text-sm text-muted-foreground truncate">{project.description || "Sem descrição"}</p>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      <span>{project.task_count}</span>
                    </div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
